import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCalendar, MatCalendarHeader, MatDateSelectionModel } from '@angular/material/datepicker';
import { MatTooltipModule } from '@angular/material/tooltip';

@Component({
  selector: 'app-today-datepicker-header',
  standalone: true,
  imports: [MatButtonModule, MatTooltipModule],
  styles: [`
    .today-button {
      min-width: 56px;
      padding-inline: 10px;
      border: 1px solid #5b91c5;
      border-radius: 18px;
      background: #e8f2fb;
      color: #286ca8;
      font-weight: 600;
    }

    .today-button:hover:not(:disabled) {
      background: #d8eafb;
    }
  `],
  template: `
    <div class="mat-calendar-header">
      <div class="mat-calendar-controls">
        <span [id]="_periodButtonLabelId" class="cdk-visually-hidden" aria-live="polite">{{ periodButtonDescription }}</span>
        <button matButton type="button" class="mat-calendar-period-button"
          (click)="currentPeriodClicked()" [attr.aria-label]="periodButtonLabel"
          [attr.aria-describedby]="_periodButtonLabelId">
          <span aria-hidden="true">{{ periodButtonText }}</span>
          <svg class="mat-calendar-arrow" [class.mat-calendar-invert]="calendar.currentView !== 'month'"
            viewBox="0 0 10 5" focusable="false" aria-hidden="true">
            <polygon points="0,0 5,5 10,0" />
          </svg>
        </button>
        <div class="mat-calendar-spacer"></div>
        <button mat-button type="button" class="today-button" [disabled]="!canSelectToday" (click)="goToToday()">Hoy</button>
        <button matIconButton type="button" class="mat-calendar-previous-button"
          [disabled]="!previousEnabled()" (click)="previousClicked()"
          [matTooltip]="prevButtonLabel" [attr.aria-label]="prevButtonLabel" disabledInteractive>
          <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
            <path d="M15.41 7.41L14 6l-6 6 6 6 1.41-1.41L10.83 12z" />
          </svg>
        </button>
        <button matIconButton type="button" class="mat-calendar-next-button"
          [disabled]="!nextEnabled()" (click)="nextClicked()"
          [matTooltip]="nextButtonLabel" [attr.aria-label]="nextButtonLabel" disabledInteractive>
          <svg viewBox="0 0 24 24" focusable="false" aria-hidden="true">
            <path d="M10 6L8.59 7.41 13.17 12l-4.58 4.59L10 18l6-6z" />
          </svg>
        </button>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TodayDatepickerHeaderComponent extends MatCalendarHeader<any> {
  private readonly adapter = inject(DateAdapter<any>);
  private readonly selectionModel = inject(MatDateSelectionModel<any, any>);

  constructor() {
    super();
  }

  get canSelectToday(): boolean {
    const today = this.adapter.today();
    return (!this.calendar.minDate || this.adapter.compareDate(today, this.calendar.minDate) >= 0)
      && (!this.calendar.maxDate || this.adapter.compareDate(today, this.calendar.maxDate) <= 0);
  }

  goToToday(): void {
    if (!this.canSelectToday) {
      return;
    }

    const today = this.adapter.today();
    this.calendar.activeDate = today;
    this.selectionModel.add(today);
  }
}
